import { MaybeRefOrGetter, toValue } from 'vue'

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query'
import { DateTime } from 'luxon'

import { employeeKeys } from '../../employees/composables/useEmployeeQuery'
import { shiftKeys } from '../../shifts/composables/useShift'

import { Area } from '../../areas/models/area'
import { Pagination } from '../../common/models/pagination'
import { EmployeeID } from '../../employees/models/employee'
import { ShiftID } from '../../shifts/models/shift'
import { SiteID } from '../../sites/models/site'
import { Skill } from '../../skills/models/skill'
import {
  EmployeeShiftOffer,
  ShiftOfferStatus,
} from '../models/employeeShiftOffer'

import { useEventStore } from '../../common/eventStore'

import {
  EmployeeShiftOfferSearchForm,
  add,
  fetch,
  fetchByEmployee,
  remove,
  update,
} from '../employeeShiftOfferService'

interface EmployeeShiftOfferFilters {
  dateFrom?: MaybeRefOrGetter<DateTime>
  dateTo?: MaybeRefOrGetter<DateTime>

  status?: MaybeRefOrGetter<string>

  area?: MaybeRefOrGetter<Area | null>
  skill?: MaybeRefOrGetter<Skill | null>
  perPage?: MaybeRefOrGetter<number>
  page?: MaybeRefOrGetter<number>
}

export const employeeShiftOfferKeys = {
  all: ['employee-shift-offers'] as const,
  lists: () => [...employeeShiftOfferKeys.all, 'list'] as const,
  list: (filters: EmployeeShiftOfferFilters) =>
    [
      ...employeeShiftOfferKeys.lists(),
      {
        area: filters.area,
        skill: filters.skill,
        perPage: filters.perPage,
        page: filters.page,
      },
    ] as const,
  listByEmployee: (
    employeeId: MaybeRefOrGetter<EmployeeID>,
    filters: EmployeeShiftOfferFilters,
  ) =>
    [
      ...employeeShiftOfferKeys.lists(),
      employeeId,
      {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        status: filters.status,
        area: filters.area,
        skill: filters.skill,
        perPage: filters.perPage,
        page: filters.page,
      },
    ] as const,
}

export function useEmployeeShiftOfferQuery(
  siteId: MaybeRefOrGetter<SiteID>,
  shiftId: MaybeRefOrGetter<ShiftID>,
  filters: EmployeeShiftOfferFilters = {},
) {
  return useQuery<
    { offers: EmployeeShiftOffer[]; pagination: Pagination },
    Error
  >({
    queryKey: employeeShiftOfferKeys.list(filters),
    queryFn: async () => {
      const form: EmployeeShiftOfferSearchForm = {
        perPage: filters.perPage ? toValue(filters.perPage) : 15,
        page: filters.page ? toValue(filters.page) : 1,
      }

      const area = toValue(filters.area)
      if (area) {
        form.areaId = area.id
      }

      const skill = toValue(filters.skill)
      if (skill) {
        form.skillId = skill.id
      }

      return fetch(toValue(siteId), toValue(shiftId), form)
    },
    placeholderData: keepPreviousData,
  })
}

export function useEmployeeShiftOfferAddMutation(
  siteId: MaybeRefOrGetter<SiteID>,
  shiftId: MaybeRefOrGetter<ShiftID>,
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, { form: { employeeId: EmployeeID } }>({
    mutationFn: async (data) => {
      return add(toValue(siteId), toValue(shiftId), data.form.employeeId)
    },
    onSettled: () => {
      // TODO: Fix invalidate only for the shift we're modifying
      queryClient.invalidateQueries({
        queryKey: employeeShiftOfferKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: employeeKeys.lists(),
      })
    },
  })
}

export function useEmployeeShiftOfferRemoveMutation() {
  const queryClient = useQueryClient()
  const eventStore = useEventStore()

  return useMutation<
    unknown,
    Error,
    { siteId: SiteID; shiftId: ShiftID; employeeId: EmployeeID }
  >({
    mutationFn: async (variables) => {
      return remove(variables.siteId, variables.shiftId, variables.employeeId)
    },
    onSettled: () => {
      // TODO: Fix invalidate only for the shift we're modifying
      queryClient.invalidateQueries({
        queryKey: employeeShiftOfferKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: employeeKeys.lists(),
      })
    },
    onError: (error) => {
      eventStore.addErrorToast({ detail: error.message })
    },
  })
}

export function useEmployeeShiftOfferUpdateMutation() {
  const queryClient = useQueryClient()
  const eventStore = useEventStore()

  return useMutation({
    mutationFn: async (variables: {
      siteId: SiteID
      shiftId: ShiftID
      employeeId: EmployeeID
      status: ShiftOfferStatus
    }) => {
      return update(variables.siteId, variables.shiftId, variables.employeeId, {
        status: variables.status,
      })
    },
    onSettled: (_data, _error, variables) => {
      // TODO: Fix invalidate only for the shift we're modifying
      queryClient.invalidateQueries({
        queryKey: employeeShiftOfferKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: employeeKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: shiftKeys.detail(variables.shiftId),
      })
    },
    onSuccess: () => {
      // TODO: We don't have access to the employee name or shift information here
      // TODO: We could change this style depending on the action we take?
      eventStore.addToast({ severity: 'info', summary: 'Updated' })
    },
    onError: (error) => {
      eventStore.addErrorToast({ detail: error.message })
    },
  })
}

export function useEmployeeShiftOfferByEmployeeQuery(
  siteId: MaybeRefOrGetter<SiteID>,
  employeeId: MaybeRefOrGetter<EmployeeID>,
  filters: EmployeeShiftOfferFilters = {},
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  return useQuery<
    { offers: EmployeeShiftOffer[]; pagination: Pagination },
    Error
  >({
    queryKey: employeeShiftOfferKeys.listByEmployee(employeeId, filters),
    queryFn: async () => {
      const form: EmployeeShiftOfferSearchForm = {
        perPage: filters.perPage ? toValue(filters.perPage) : 15,
        page: filters.page ? toValue(filters.page) : 1,
      }

      const dateFrom = toValue(filters.dateFrom)
      if (dateFrom) {
        form.dateFrom = dateFrom
      }

      const dateTo = toValue(filters.dateTo)
      if (dateTo) {
        form.dateTo = dateTo
      }

      const status = toValue(filters.status)
      if (status) {
        form.status = status
      }

      const area = toValue(filters.area)
      if (area) {
        form.areaId = area.id
      }

      const skill = toValue(filters.skill)
      if (skill) {
        form.skillId = skill.id
      }

      return fetchByEmployee(toValue(siteId), toValue(employeeId), form)
    },
    placeholderData: keepPreviousData,
    enabled: toValue(enabled),
  })
}
